import {
    Box,
    Checkbox,
    ListItemText,
    SelectChangeEvent,
    TextField,
} from '@mui/material'
import dayjs from 'dayjs'
import { FormField } from 'global/components/Form'
import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { funcionario } from 'service/api'
import { useStore } from 'store'
import Menu from '../MenuV2'
import { filterLocaisCentro } from './filter-select'
import { SelectProps } from './types'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
}

type Props = Omit<SelectProps, 'buttom' | 'isSupervisor'> & {
    checked?: number[]
    onChange?: (ids: number[]) => void
    defaultValues?: number[]
    filterCentroCusto?: boolean
    value?: number[]
}

export const SelectFuncionarios: React.FC<Props> = ({
    size = 'small',
    form,
    children,
    disabled,
    checked,
    onChange,
    defaultValues = [],
    filterCentroCusto,
    value,
    width = 260,
    ...props
}) => {
    const formContext = useFormContext()
    const companyId = useStore(state => state.companyId)
    const centroCustoId = useStore(state => state.costCenterId)
    const selectedDate = useStore(state => state.selectedDate)

    const { data } = funcionario.useFetch({
        queryParams: { empresaId: companyId },
    })

    const [_employeeIds, _setEmployeeIds] = useState<number[]>(defaultValues)
    const employeeIds = value ? value : _employeeIds
    const setEmployeeIds = value && onChange ? onChange : _setEmployeeIds

    const dataCompetencia = dayjs(selectedDate).format('YYYY-DD-MM')

    const funcionarioAtivos = (
        data?.filter(
            ({ dtRescisao }) =>
                !dtRescisao || dayjs(dtRescisao).isBefore(dataCompetencia),
        ) || []
    ).filter(filterLocaisCentro([], [centroCustoId], item => item))

    useEffect(() => {
        if (filterCentroCusto) setEmployeeIds([])
    }, [centroCustoId])

    const isEverythingSelected =
        employeeIds.length === funcionarioAtivos.length + (checked?.length || 0)

    const handleChange = (event: SelectChangeEvent<unknown>) => {
        const {
            target: { value },
        } = event

        if ((value as any[]).includes('todos')) {
            if (isEverythingSelected) {
                setEmployeeIds([])
            } else {
                const ids = funcionarioAtivos
                    ?.map(({ id }) => id)
                    .filter(id => !checked?.includes(id))

                setEmployeeIds(ids || [])
                onChange?.(ids || [])
            }
        } else {
            setEmployeeIds(value as number[])
            onChange?.(value as number[])
        }
    }

    useEffect(() => {
        if (form && formContext) {
            formContext.setValue('funcionarioIds', employeeIds)
        }
    }, [employeeIds])

    return (
        <Box display="flex" alignItems="center" gap={1} width={width}>
            {form ? (
                <FormField
                    name="funcionarioIds"
                    label="Funcionários"
                    size={size}
                    select
                    disabled={disabled}
                    helperText=" "
                    fullWidth
                    {...props}
                    SelectProps={{
                        multiple: true,
                        value: employeeIds,
                        onChange: handleChange,
                        renderValue: selected => {
                            return funcionarioAtivos
                                .filter(({ id }) => !checked?.includes(id))
                                .filter(it =>
                                    (selected as number[]).some(
                                        select => select === it.id,
                                    ),
                                )
                                .map(({ nome }) => nome)
                                .join(', ')
                        },
                        MenuProps,
                    }}
                >
                    {children}
                    {funcionarioAtivos && funcionarioAtivos?.length > 0 && (
                        <Menu.Item key="employee-todos" value="todos">
                            <Checkbox checked={isEverythingSelected} />
                            <ListItemText primary="Todos" />
                        </Menu.Item>
                    )}
                    {funcionarioAtivos?.map(({ id, nome }, index) => {
                        return (
                            <Menu.Item
                                key={`employee-${index}`}
                                value={id}
                                disabled={checked?.includes(id)}
                            >
                                <Checkbox
                                    checked={
                                        checked?.includes(id) ||
                                        employeeIds.some(it => it === id)
                                    }
                                />
                                <ListItemText primary={nome} />
                            </Menu.Item>
                        )
                    })}
                </FormField>
            ) : (
                <TextField
                    label="Funcionários"
                    variant="filled"
                    SelectProps={{
                        multiple: true,
                        value: employeeIds,
                        onChange: handleChange,
                        renderValue: selected => {
                            return funcionarioAtivos
                                ?.filter(({ id }) => !checked?.includes(id))
                                ?.filter(it =>
                                    (selected as number[]).some(
                                        select => select === it.id,
                                    ),
                                )
                                .map(({ nome }) => nome)
                                .join(', ')
                        },
                        MenuProps,
                    }}
                    select
                    fullWidth
                    disabled={disabled}
                    size={size}
                    {...props}
                >
                    {children}
                    {funcionarioAtivos && funcionarioAtivos?.length > 0 && (
                        <Menu.Item key="employee-todos" value="todos">
                            <Checkbox />
                            <ListItemText primary="Todos" />
                        </Menu.Item>
                    )}
                    {funcionarioAtivos?.map(({ id, nome }, index) => {
                        return (
                            <Menu.Item
                                key={`employee-${index}`}
                                value={id}
                                disabled={checked?.includes(id)}
                            >
                                <Checkbox
                                    checked={
                                        checked?.includes(id) ||
                                        employeeIds.some(it => it === id)
                                    }
                                />
                                <ListItemText primary={nome} />
                            </Menu.Item>
                        )
                    })}
                </TextField>
            )}
        </Box>
    )
}
